import { isAnyOf, Middleware } from '@reduxjs/toolkit';
import { DeviceVariantsEnum } from '@ubnt/link-core/enums';
import { deviceInstanceGetter } from '@ubnt/wifi-calculator/device/deviceInstanceGetter';
import {
  countrySupportsAfc,
  countrySupportsBand,
  countrySupportsChannelWidth,
  frequencySupportsChannelWidth,
  getFrequency,
  getMaxSupportedChannelWidth,
  getSupportedChannel,
  getBandByVariant,
} from '@ubnt/wifi-calculator/utils/regulatoryLimits';
import {
  getSupportedVariant,
  isEnumValue,
  isSupportedVariant,
  getDeviceNameByCountry,
} from '@ubnt/wifi-calculator/utils';
import {
  devicesCustomClientDeviceInstanceMapSelector,
  devicesDeviceAfcSelector,
  devicesDeviceApIdsSelector,
  devicesDeviceChannelWidthSelector,
  devicesDeviceIsOutdoorModeSelector,
  devicesDeviceNameSelector,
  devicesDeviceStateChannelSelector,
  devicesDeviceUpdate,
  devicesDeviceVariantParamsUpdate,
  devicesDeviceVariantSelector,
  devicesOutdoorToggle,
  devicesAfcChange,
  getChannel,
  devicesDeviceChange,
  devicesUpdateCustomClientDevices,
} from 'modules/devices/slice';
import { RootState } from 'app/store/reducer';
import { settingsCountrySelector, settingsCountryUpdate } from 'modules/settings/slice';
import { CLIENT_ID } from 'modules/devices/constants';

const isParamsUpdateAction = isAnyOf(settingsCountryUpdate, devicesOutdoorToggle, devicesAfcChange);
const isClientParamsUpdateAction = isAnyOf(devicesUpdateCustomClientDevices);

export const devicesMiddleware: Middleware<unknown, RootState> = (store) => (next) => (action) => {
  let result;
  const isClientParamsUpdate = isClientParamsUpdateAction(action);

  if (isClientParamsUpdate) {
    result = next(action);
  }

  if (isParamsUpdateAction(action) || isClientParamsUpdate) {
    const state = store.getState();
    const country = 'country' in action.payload ? action.payload.country : settingsCountrySelector(state);
    const newCountrySupportsAfc = countrySupportsAfc(country);
    const dispatch = store.dispatch;
    const apIds = devicesDeviceApIdsSelector(state);
    const clientDeviceName = devicesDeviceNameSelector(state, CLIENT_ID);
    const externalDeviceMap = devicesCustomClientDeviceInstanceMapSelector(state);
    const clientInstance = deviceInstanceGetter(clientDeviceName, undefined, externalDeviceMap);

    apIds.forEach((apId: string) => {
      const deviceName = devicesDeviceNameSelector(state, apId);
      const deviceInstance = deviceInstanceGetter(deviceName, undefined, externalDeviceMap);
      const variant = devicesDeviceVariantSelector(state, apId);
      const isOutdoorModeState = devicesDeviceIsOutdoorModeSelector(state, apId);
      const isOutdoorModeAction = action.type === devicesOutdoorToggle.toString();
      const isOutdoorMode = isOutdoorModeAction ? !isOutdoorModeState : isOutdoorModeState;
      const isAfcEnabled =
        'afc' in action.payload ?
          action.payload.afc
          // oxlint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        : (deviceInstance.supportsAfc && isOutdoorMode && newCountrySupportsAfc) ||
          devicesDeviceAfcSelector(state, apId);
      const isVariantNotSupported =
        variant == null ? false : (
          !countrySupportsBand(country, getBandByVariant(variant), isOutdoorMode, isAfcEnabled) ||
          !isSupportedVariant(clientInstance.variants, variant)
        );
      if (variant == null || isVariantNotSupported) {
        const newVariant = getSupportedVariant(deviceInstance, clientInstance, country, isOutdoorMode, isAfcEnabled);
        if (variant !== newVariant) {
          dispatch(devicesDeviceUpdate({ id: apId, changes: { variant: newVariant } }));
        }
      }

      deviceInstance.variants.forEach((variantNumber) => {
        if (isEnumValue(DeviceVariantsEnum, variantNumber)) {
          const band = getBandByVariant(variantNumber);

          if (countrySupportsBand(country, band, isOutdoorMode, isAfcEnabled)) {
            const variantDeviceInstance = deviceInstanceGetter(deviceName, variantNumber, externalDeviceMap);
            const stateChannelWidth = devicesDeviceChannelWidthSelector(state, apId, variantNumber);
            const stateChannel = devicesDeviceStateChannelSelector(state, apId, variantNumber);
            let channelWidth = stateChannelWidth;
            if (channelWidth == null) {
              return;
            }

            if (!countrySupportsChannelWidth(country, band, channelWidth, isOutdoorMode, isAfcEnabled)) {
              channelWidth = getMaxSupportedChannelWidth(country, band, isOutdoorMode, isAfcEnabled);
            }

            let channel = getChannel(variantDeviceInstance, stateChannel, channelWidth);
            const channelsMap = variantDeviceInstance.channels;

            if (
              channel &&
              channelsMap?.[channel] &&
              !frequencySupportsChannelWidth(
                country,
                getFrequency(channelsMap[channel]),
                channelWidth,
                isAfcEnabled,
                isOutdoorMode
              )
            ) {
              channel = getSupportedChannel(country, channelsMap, channelWidth, isAfcEnabled, isOutdoorMode);
            }

            if (channelWidth !== stateChannelWidth || channel !== stateChannel) {
              dispatch(
                devicesDeviceVariantParamsUpdate({
                  id: apId,
                  variant: variantNumber,
                  changes: { channel, channelWidth },
                })
              );
            }
          }
        }
      });

      const newDeviceName = getDeviceNameByCountry(deviceName, country);
      if (deviceName !== newDeviceName) {
        dispatch(devicesDeviceChange({ id: apId, name: newDeviceName, country }));
      }
    });
  }

  return result ?? next(action);
};
